import { Injectable } from '@angular/core';
import { NumberUtils } from '@modeso/aumico-lib-core-fe';
import {
    AccountType,
    IAccount,
    ICashFlowFinancingActivities,
    ICashFlowOperatingActivities,
    ICashFlowStatement,
    ICashFlowStatementEntry,
    ICashFlowTotal,
    IDisplayOptions,
} from '@modeso/types__aumico-lib-annualreport-be';
import { FinancialLineItemCashFlowType, FinancialLineItemType } from '@modeso/types__aumico-lib-settings-be';
import { cloneDeep } from 'lodash-es';
import { ICurrentAndPreviousFlis } from '../../../interfaces/ICurrentAndPreviousMappedFlis.interface';

@Injectable({ providedIn: 'root' })
export class CashFlowAdjustmentsAndReclassesHelper {
    /**
     * The display convention the cash flow step itself uses for the values it stores:
     * it takes the FLI's persisted closing/opening balance plus its reClass amounts
     * (see CashFlowStatementHelper.prepareCashFlowFlisFromPreviousCashFlowStatement),
     * while adjustments stay in their own field and are only folded in for printing.
     *
     * It is the reference point for re-applying the print display options to a previous
     * year value without re-deriving that value from the previous year's report.
     */
    private static readonly CASH_FLOW_STEP_DISPLAY_CONVENTION: Pick<
        IDisplayOptions,
        'showReClassInSums' | 'showAdjustmentInSums'
    > = {
        showReClassInSums: true,
        showAdjustmentInSums: false,
    };

    public applyAdjustMentsAndReclasses(
        cashFlow: ICashFlowStatement | null,
        options: IDisplayOptions,
        flis: ICurrentAndPreviousFlis,
        fliIdToType: Map<string, FinancialLineItemType>,
    ): ICashFlowStatement | null {
        if (cashFlow == null || Object.keys(cashFlow).length === 0) {
            return null;
        }
        const updatedCashFlow = cloneDeep<ICashFlowStatement>(cashFlow);

        this.applyAdjustementsAndReclassesOnOperatingActivities(
            updatedCashFlow.operatingActivities,
            options,
            flis,
            fliIdToType,
        );
        this.applyAdjustementsAndReclassesOnFinancingActivities(updatedCashFlow.financingActivities, options, flis);

        this.applyAdjustmentsAndReclassesOfCashFli(updatedCashFlow.total, options, flis);

        return updatedCashFlow;
    }

    /**
     * Re-applies the print display options to a previous year value WITHOUT re-deriving it from
     * the previous year's report.
     *
     * The previous year column of the cash flow statement is a snapshot: the cash flow step stores
     * it when the report is saved and never recalculates it afterwards. Printing must not calculate
     * it from the previous year's account mapping either - that mapping can have changed since,
     * which made the print preview disagree with the cash flow step (e.g. a Jahresergebnis that
     * still shows in the step but printed as 0 after its accounts were unmapped in the previous
     * year's report).
     *
     * Printing does have to honour showAdjustmentInSums / showReClassInSums though, so the saved
     * value is kept as the base and only the DIFFERENCE the display options make is added on top.
     * Because both terms of that difference are calculated from the same (previous year) accounts,
     * any drift in the underlying values cancels out - only the option dependent part survives.
     */
    private applyPrintOptionsToPreviousValue(
        savedPreviousValue: number | null | undefined,
        calculateWithDisplayOptions: (displayOptions: IDisplayOptions) => number | null,
        options: IDisplayOptions,
    ): number | null {
        const valueWithPrintOptions = calculateWithDisplayOptions(options);
        const valueWithStepConvention = calculateWithDisplayOptions({
            ...options,
            ...CashFlowAdjustmentsAndReclassesHelper.CASH_FLOW_STEP_DISPLAY_CONVENTION,
        });

        if (savedPreviousValue == null || !NumberUtils.isFinite(savedPreviousValue)) {
            // Nothing was saved for this row in the cash flow step (e.g. an FLI that only exists in
            // the current year) - keep it empty here as well instead of inventing a value.
            return savedPreviousValue ?? null;
        }

        if (
            valueWithPrintOptions == null ||
            !NumberUtils.isFinite(valueWithPrintOptions) ||
            valueWithStepConvention == null ||
            !NumberUtils.isFinite(valueWithStepConvention)
        ) {
            // The print options cannot have an impact we could calculate (e.g. the FLI has no
            // accounts in the previous year's report) - the saved value stands as it is.
            return NumberUtils.roundNumber(savedPreviousValue);
        }

        const printOptionsImpact = NumberUtils.subtractNumbers(valueWithPrintOptions, valueWithStepConvention);

        if (!NumberUtils.isFinite(printOptionsImpact) || NumberUtils.isZero(printOptionsImpact)) {
            return NumberUtils.roundNumber(savedPreviousValue);
        }

        return (
            NumberUtils.sumIgnoringInfiniteNumbers([savedPreviousValue, printOptionsImpact], undefined) ??
            NumberUtils.roundNumber(savedPreviousValue)
        );
    }

    private applyAdjustementsAndReclassesOnOperatingActivities(
        operatingActivities: ICashFlowOperatingActivities,
        options: IDisplayOptions,
        flis: ICurrentAndPreviousFlis,
        fliIdToType: Map<string, FinancialLineItemType>,
    ): void {
        operatingActivities.entries = operatingActivities.entries.map((fliEntry) => {
            // operating activities equation(calculation)
            const fliAccountsOfCurrentYear = flis.currentFliIdToAccounts.get(fliEntry.fliId);
            const fliType = fliIdToType.get(fliEntry.fliId);
            const optionsWithoutReclasses = {
                ...options,
                showReClassInSums: false,
            };

            // if accounts of fli are not found, that means this is a manually added row by user
            if (fliAccountsOfCurrentYear == null) {
                return fliEntry;
            }
            const currentClosingValueWitAdjustementsAndReclasses = this.calculateCloseValueWithAdjustementsAndReclasses(
                fliAccountsOfCurrentYear,
                options,
            );

            const currentClosingValueWithoutReclasses = this.calculateCloseValueWithAdjustementsAndReclasses(
                fliAccountsOfCurrentYear,
                optionsWithoutReclasses,
            );

            const currentOpenValueWithoutReclasses = this.calculateOpenValueWithAdjustementsAndReclasses(
                fliAccountsOfCurrentYear,
                optionsWithoutReclasses,
            );

            if (
                fliEntry.manuallyEnteredCurrentValue != null &&
                NumberUtils.isFinite(fliEntry.manuallyEnteredCurrentValue)
            ) {
                let currentReclassesImpact: number | null = null;
                if (options.showReClassInSums) {
                    if (fliEntry.type === FinancialLineItemCashFlowType.NETWORKING_CAPITAL) {
                        const currentOpenValueWithAdjustementsAndReclasses =
                            this.calculateOpenValueWithAdjustementsAndReclasses(fliAccountsOfCurrentYear, options);
                        currentReclassesImpact = this.calculateNetworkingCapital(
                            NumberUtils.subtractNumbers(
                                currentClosingValueWitAdjustementsAndReclasses ?? 0,
                                currentClosingValueWithoutReclasses ?? 0,
                            ),
                            NumberUtils.subtractNumbers(
                                currentOpenValueWithAdjustementsAndReclasses ?? 0,
                                currentOpenValueWithoutReclasses ?? 0,
                            ),
                            fliType,
                        );
                    } else if (fliEntry.type === FinancialLineItemCashFlowType.OPERATIONS) {
                        currentReclassesImpact = NumberUtils.multiplyNumbers([
                            NumberUtils.subtractNumbers(
                                currentClosingValueWitAdjustementsAndReclasses ?? 0,
                                currentClosingValueWithoutReclasses ?? 0,
                            ),
                            -1,
                        ]);
                    } else {
                        currentReclassesImpact = NumberUtils.subtractNumbers(
                            currentClosingValueWitAdjustementsAndReclasses ?? 0,
                            currentClosingValueWithoutReclasses ?? 0,
                        );
                    }
                }

                if (currentReclassesImpact != null && NumberUtils.isFinite(currentReclassesImpact)) {
                    fliEntry.manuallyEnteredCurrentValue = NumberUtils.sumIgnoringInfiniteNumbers(
                        [fliEntry.manuallyEnteredCurrentValue, currentReclassesImpact],
                        undefined,
                    );
                }
            }

            if (
                fliEntry.manuallyEnteredPreviousValue != null &&
                NumberUtils.isFinite(fliEntry.manuallyEnteredPreviousValue)
            ) {
                let previousReclassesImpact: number | null = null;
                if (options.showReClassInSums && flis.previousFliIdToAccounts.size !== 0) {
                    if (fliEntry.type === FinancialLineItemCashFlowType.NETWORKING_CAPITAL) {
                        const previousValueWithReclasses = this.calculateNetworkingCapitalValueOfPreviousYear(
                            fliEntry,
                            flis.previousFliIdToAccounts,
                            options,
                            fliType,
                        );
                        const previousValueWithoutReclasses = this.calculateNetworkingCapitalValueOfPreviousYear(
                            fliEntry,
                            flis.previousFliIdToAccounts,
                            optionsWithoutReclasses,
                            fliType,
                        );
                        previousReclassesImpact = NumberUtils.subtractNumbers(
                            previousValueWithReclasses ?? 0,
                            previousValueWithoutReclasses ?? 0,
                        );
                    } else if (fliEntry.type === FinancialLineItemCashFlowType.OPERATIONS) {
                        const previousValueWithReclasses = this.calculatePreviousValueOfOperationsWithAdjustementsAndReclasses(
                            fliEntry,
                            flis.previousFliIdToAccounts,
                            options,
                        );
                        const previousValueWithoutReclasses =
                            this.calculatePreviousValueOfOperationsWithAdjustementsAndReclasses(
                                fliEntry,
                                flis.previousFliIdToAccounts,
                                optionsWithoutReclasses,
                            );
                        previousReclassesImpact = NumberUtils.subtractNumbers(
                            previousValueWithReclasses ?? 0,
                            previousValueWithoutReclasses ?? 0,
                        );
                    } else {
                        const previousValueWithReclasses = this.calculatePreviousValueOfProfitWithAdjustementsAndReclasses(
                            fliEntry,
                            flis.previousFliIdToAccounts,
                            options,
                        );
                        const previousValueWithoutReclasses =
                            this.calculatePreviousValueOfProfitWithAdjustementsAndReclasses(
                                fliEntry,
                                flis.previousFliIdToAccounts,
                                optionsWithoutReclasses,
                            );
                        previousReclassesImpact = NumberUtils.subtractNumbers(
                            previousValueWithReclasses ?? 0,
                            previousValueWithoutReclasses ?? 0,
                        );
                    }
                }

                if (previousReclassesImpact != null && NumberUtils.isFinite(previousReclassesImpact)) {
                    fliEntry.manuallyEnteredPreviousValue = NumberUtils.sumIgnoringInfiniteNumbers(
                        [fliEntry.manuallyEnteredPreviousValue, previousReclassesImpact],
                        undefined,
                    );
                }
            }

            if (fliEntry.type === FinancialLineItemCashFlowType.OPERATIONS) {
                // operation equation (calculation: closing value * -1)
                let calculatedCurrentValue: number | null = null;

                if (
                    currentClosingValueWitAdjustementsAndReclasses != null &&
                    NumberUtils.isFinite(currentClosingValueWitAdjustementsAndReclasses)
                ) {
                    calculatedCurrentValue = NumberUtils.multiplyNumbers([
                        currentClosingValueWitAdjustementsAndReclasses,
                        -1,
                    ]);
                }
                fliEntry.calculatedCurrentValue = calculatedCurrentValue;

                fliEntry.calculatedPreviousValue = this.applyPrintOptionsToPreviousValue(
                    fliEntry.calculatedPreviousValue,
                    (displayOptions) =>
                        this.calculatePreviousValueOfOperationsWithAdjustementsAndReclasses(
                            fliEntry,
                            flis.previousFliIdToAccounts,
                            displayOptions,
                        ),
                    options,
                );
            } else if (fliEntry.type === FinancialLineItemCashFlowType.NETWORKING_CAPITAL) {
                const openValueWithAdjustmentAndReclasses = this.calculateOpenValueWithAdjustementsAndReclasses(
                    fliAccountsOfCurrentYear,
                    options,
                );
                fliEntry.calculatedCurrentValue = this.calculateNetworkingCapital(
                    currentClosingValueWitAdjustementsAndReclasses,
                    openValueWithAdjustmentAndReclasses,
                    fliType,
                );
                fliEntry.calculatedPreviousValue = this.applyPrintOptionsToPreviousValue(
                    fliEntry.calculatedPreviousValue,
                    (displayOptions) =>
                        this.calculateNetworkingCapitalValueOfPreviousYear(
                            fliEntry,
                            flis.previousFliIdToAccounts,
                            displayOptions,
                            fliType,
                        ),
                    options,
                );
            } else {
                fliEntry.calculatedCurrentValue = currentClosingValueWitAdjustementsAndReclasses;
                fliEntry.calculatedPreviousValue = this.applyPrintOptionsToPreviousValue(
                    fliEntry.calculatedPreviousValue,
                    (displayOptions) =>
                        this.calculatePreviousValueOfProfitWithAdjustementsAndReclasses(
                            fliEntry,
                            flis.previousFliIdToAccounts,
                            displayOptions,
                        ),
                    options,
                );
            }
            return fliEntry;
        });
    }

    private calculatePreviousValueOfOperationsWithAdjustementsAndReclasses(
        fliEntry: ICashFlowStatementEntry,
        fliIdToAccounts: Map<string, IAccount[]>,
        options: IDisplayOptions,
    ): number | null {
        if (fliIdToAccounts.size === 0) {
            return fliEntry.calculatedPreviousValue;
        }
        const fliAccounts = fliIdToAccounts.get(fliEntry.fliId);
        const closeValueWithAdjustementsAndReclasses = this.calculateCloseValueWithAdjustementsAndReclasses(
            fliAccounts,
            options,
        );

        if (
            closeValueWithAdjustementsAndReclasses != null &&
            NumberUtils.isFinite(closeValueWithAdjustementsAndReclasses)
        ) {
            return NumberUtils.multiplyNumbers([closeValueWithAdjustementsAndReclasses, -1]);
        }
        return null;
    }

    private calculatePreviousValueOfProfitWithAdjustementsAndReclasses(
        fliEntry: ICashFlowStatementEntry,
        fliIdToAccounts: Map<string, IAccount[]>,
        options: IDisplayOptions,
    ): number | null {
        if (fliIdToAccounts.size === 0) {
            return fliEntry.calculatedPreviousValue;
        }

        const fliAccounts = fliIdToAccounts.get(fliEntry.fliId);

        return this.calculateCloseValueWithAdjustementsAndReclasses(fliAccounts, options);
    }

    private calculateNetworkingCapital(
        closeValue: number | null,
        openValue: number | null,
        type: FinancialLineItemType,
    ): number | null {
        // network capital equation
        // calculation: * if of type assets: opening - closing
        // * if of type liabilities: closing- opening

        if (
            (closeValue == null || !NumberUtils.isFinite(closeValue)) &&
            (openValue == null || !NumberUtils.isFinite(openValue))
        ) {
            return null;
        }

        switch (type) {
            case FinancialLineItemType.ASSETS:
                return NumberUtils.subtractNumbers(openValue ?? 0, closeValue ?? 0);
            case FinancialLineItemType.LIABILITIES:
                return NumberUtils.subtractNumbers(closeValue ?? 0, openValue ?? 0);
            default:
                throw new Error(`This fli's type ${type} is not supported`);
        }
    }

    private calculateNetworkingCapitalValueOfPreviousYear(
        fliEntry: ICashFlowStatementEntry,
        fliIdToAccounts: Map<string, IAccount[]>,
        options: IDisplayOptions,
        fliType: FinancialLineItemType,
    ): number | null {
        if (fliIdToAccounts.size === 0) {
            return fliEntry.calculatedPreviousValue;
        }
        const fliAccountsOfPreviousYear = fliIdToAccounts.get(fliEntry.fliId);
        const closingValueWithAdjustmentsAndReclasses = this.calculateCloseValueWithAdjustementsAndReclasses(
            fliAccountsOfPreviousYear,
            options,
        );
        const openingValueWithAdjustmentsAndReclasses = this.calculateOpenValueWithAdjustementsAndReclasses(
            fliAccountsOfPreviousYear,
            options,
        );
        return this.calculateNetworkingCapital(
            closingValueWithAdjustmentsAndReclasses,
            openingValueWithAdjustmentsAndReclasses,
            fliType,
        );
    }

    private applyAdjustementsAndReclassesOnFinancingActivities(
        financingActivities: ICashFlowFinancingActivities,
        options: IDisplayOptions,
        flis: ICurrentAndPreviousFlis,
    ): void {
        financingActivities.entries = financingActivities.entries.map((fliEntry) => {
            const fliAccountsOfCurrentYear = flis.currentFliIdToAccounts.get(fliEntry.fliId);
            const optionsWithoutReclasses = {
                ...options,
                showReClassInSums: false,
            };

            // if accounts of fli are not found, that means this is a manually added row by user
            if (fliAccountsOfCurrentYear == null) {
                return fliEntry;
            }

            const currentClosingValueWitAdjustementsAndReclasses = this.calculateCloseValueWithAdjustementsAndReclasses(
                fliAccountsOfCurrentYear,
                options,
            );

            const currentOpenValueWithAdjustementsAndReclasses = this.calculateOpenValueWithAdjustementsAndReclasses(
                fliAccountsOfCurrentYear,
                options,
            );

            const currentClosingValueWithoutReclasses = this.calculateCloseValueWithAdjustementsAndReclasses(
                fliAccountsOfCurrentYear,
                optionsWithoutReclasses,
            );

            const currentOpenValueWithoutReclasses = this.calculateOpenValueWithAdjustementsAndReclasses(
                fliAccountsOfCurrentYear,
                optionsWithoutReclasses,
            );

            let calculatedCurrentValue: number | null = null;
            if (
                (currentClosingValueWitAdjustementsAndReclasses != null &&
                    NumberUtils.isFinite(currentClosingValueWitAdjustementsAndReclasses)) ||
                (currentOpenValueWithAdjustementsAndReclasses != null &&
                    NumberUtils.isFinite(currentOpenValueWithAdjustementsAndReclasses))
            ) {
                calculatedCurrentValue = NumberUtils.subtractNumbers(
                    currentClosingValueWitAdjustementsAndReclasses ?? 0,
                    currentOpenValueWithAdjustementsAndReclasses ?? 0,
                );
            }

            // financing: close value - open value
            fliEntry.calculatedCurrentValue = calculatedCurrentValue;

            if (
                fliEntry.manuallyEnteredCurrentValue != null &&
                NumberUtils.isFinite(fliEntry.manuallyEnteredCurrentValue) &&
                options.showReClassInSums
            ) {
                const currentReclassesImpact = NumberUtils.subtractNumbers(
                    NumberUtils.subtractNumbers(
                        currentClosingValueWitAdjustementsAndReclasses ?? 0,
                        currentOpenValueWithAdjustementsAndReclasses ?? 0,
                    ),
                    NumberUtils.subtractNumbers(
                        currentClosingValueWithoutReclasses ?? 0,
                        currentOpenValueWithoutReclasses ?? 0,
                    ),
                );

                if (currentReclassesImpact != null && NumberUtils.isFinite(currentReclassesImpact)) {
                    fliEntry.manuallyEnteredCurrentValue = NumberUtils.sumIgnoringInfiniteNumbers(
                        [fliEntry.manuallyEnteredCurrentValue, currentReclassesImpact],
                        undefined,
                    );
                }
            }

            if (flis.previousFliIdToAccounts.size !== 0) {
                const fliAccountsOfPreviousYear = flis.previousFliIdToAccounts.get(fliEntry.fliId);
                // calculate financing activities of previous year to be previous year value of current year
                const calculatedPreviousFinancingActivitiesValue = this.calculatePreviousValueOfFinancingActivities(
                    fliAccountsOfPreviousYear,
                    options,
                );

                fliEntry.calculatedPreviousValue = this.applyPrintOptionsToPreviousValue(
                    fliEntry.calculatedPreviousValue,
                    (displayOptions) =>
                        this.calculatePreviousValueOfFinancingActivities(fliAccountsOfPreviousYear, displayOptions),
                    options,
                );

                if (
                    fliEntry.manuallyEnteredPreviousValue != null &&
                    NumberUtils.isFinite(fliEntry.manuallyEnteredPreviousValue) &&
                    options.showReClassInSums
                ) {
                    const calculatedPreviousValueWithoutReclasses = this.calculatePreviousValueOfFinancingActivities(
                        fliAccountsOfPreviousYear,
                        optionsWithoutReclasses,
                    );
                    const previousReclassesImpact = NumberUtils.subtractNumbers(
                        calculatedPreviousFinancingActivitiesValue ?? 0,
                        calculatedPreviousValueWithoutReclasses ?? 0,
                    );

                    if (previousReclassesImpact != null && NumberUtils.isFinite(previousReclassesImpact)) {
                        fliEntry.manuallyEnteredPreviousValue = NumberUtils.sumIgnoringInfiniteNumbers(
                            [fliEntry.manuallyEnteredPreviousValue, previousReclassesImpact],
                            undefined,
                        );
                    }
                }
            }
            return fliEntry;
        });
    }

    private applyAdjustmentsAndReclassesOfCashFli(
        cashAndTotals: ICashFlowTotal,
        options: IDisplayOptions,
        flis: ICurrentAndPreviousFlis,
    ): void {
        const fliId = cashAndTotals.cashAndCashEquivalentsFliId;
        const fliAccountOfCurrentYear = flis.currentFliIdToAccounts.get(fliId);

        cashAndTotals.closingCashAndCashEquivalents = this.calculateCloseValueWithAdjustementsAndReclasses(
            fliAccountOfCurrentYear,
            options,
        );

        cashAndTotals.openingCashAndCashEquivalents = this.calculateOpenValueWithAdjustementsAndReclasses(
            fliAccountOfCurrentYear,
            options,
        );

        if (flis.previousFliIdToAccounts.size !== 0) {
            const fliAccountsOfPreviousYear = flis.previousFliIdToAccounts.get(fliId);
            cashAndTotals.previousClosingCashAndCashEquivalents = this.applyPrintOptionsToPreviousValue(
                cashAndTotals.previousClosingCashAndCashEquivalents,
                (displayOptions) =>
                    this.calculateCloseValueWithAdjustementsAndReclasses(fliAccountsOfPreviousYear, displayOptions),
                options,
            );

            cashAndTotals.previousOpeningCashAndCashEquivalents = this.applyPrintOptionsToPreviousValue(
                cashAndTotals.previousOpeningCashAndCashEquivalents,
                (displayOptions) =>
                    this.calculateOpenValueWithAdjustementsAndReclasses(fliAccountsOfPreviousYear, displayOptions),
                options,
            );
        }
    }

    private calculatePreviousValueOfFinancingActivities(
        fliAccounts: IAccount[],
        options: IDisplayOptions,
    ): number | null {
        if (fliAccounts == null) return null;
        const closingValueWithAdjustmentsAndReclasses = this.calculateCloseValueWithAdjustementsAndReclasses(
            fliAccounts,
            options,
        );
        const openingValueWithAdjustmentsAndReclasses = this.calculateOpenValueWithAdjustementsAndReclasses(
            fliAccounts,
            options,
        );

        let previousValueOfFinancingActivitiesValue: number | null = null;
        if (
            (closingValueWithAdjustmentsAndReclasses != null &&
                NumberUtils.isFinite(closingValueWithAdjustmentsAndReclasses)) ||
            (openingValueWithAdjustmentsAndReclasses != null &&
                NumberUtils.isFinite(openingValueWithAdjustmentsAndReclasses))
        ) {
            previousValueOfFinancingActivitiesValue = NumberUtils.subtractNumbers(
                closingValueWithAdjustmentsAndReclasses ?? 0,
                openingValueWithAdjustmentsAndReclasses ?? 0,
            );
        }
        return previousValueOfFinancingActivitiesValue;
    }

    private calculateCloseValueWithAdjustementsAndReclasses(
        fliAccounts: IAccount[],
        options: IDisplayOptions,
    ): number | null {
        if (fliAccounts == null) return null;

        const accountsOfFli = fliAccounts.filter(
            (account) =>
                account.type === AccountType.BALANCE ||
                account.type === AccountType.INCOME ||
                account.type === AccountType.SPECIAL_ACCOUNT_DELETE_ON_MAPPING,
        );
        const reclassesAccount = fliAccounts.filter((account) => account.type === AccountType.RECLASSES);

        const closingValueOfAccounts = this.calculateTotalClosingValueOfAccounts(accountsOfFli);
        const closingAdjustmentsAndReclassesValues = this.calculateClosingAdjustmentsAndReclassesValueOfFli(
            accountsOfFli,
            reclassesAccount,
            options,
        );
        let closeValueWithAdjustementsAndReclasses: number | null = null;

        const closeValue = NumberUtils.sumIgnoringInfiniteNumbers(
            [closingValueOfAccounts, closingAdjustmentsAndReclassesValues],
            undefined,
        );
        if (closeValue != null) {
            closeValueWithAdjustementsAndReclasses = closeValue;
        }
        return closeValueWithAdjustementsAndReclasses;
    }

    private calculateOpenValueWithAdjustementsAndReclasses(
        fliAccounts: IAccount[] | undefined,
        options: IDisplayOptions,
    ): number | null {
        if (fliAccounts == null) return null;
        const accountsOfFli = fliAccounts?.filter(
            (account) =>
                account.type === AccountType.BALANCE ||
                account.type === AccountType.INCOME ||
                account.type === AccountType.SPECIAL_ACCOUNT_DELETE_ON_MAPPING,
        );
        const reclassesAccount = fliAccounts?.filter((account) => account.type === AccountType.RECLASSES);

        const openingValueOfAccounts = this.calculateTotalOpeningValueOfAccounts(accountsOfFli);
        const openingAdjustmentsAndReclassesValues = this.calculateOpeningAdjustmentsAndReclassesValueOfFli(
            accountsOfFli,
            reclassesAccount,
            options,
        );

        let openValueWithAdjustementsAndReclasses: number | null = null;
        const openValue = NumberUtils.sumIgnoringInfiniteNumbers(
            [openingValueOfAccounts, openingAdjustmentsAndReclassesValues],
            undefined,
        );
        if (openValue != null) {
            openValueWithAdjustementsAndReclasses = openValue;
        }
        return openValueWithAdjustementsAndReclasses;
    }

    private calculateClosingAdjustmentsAndReclassesValueOfFli(
        fliAccounts: IAccount[],
        reclassesAccounts: IAccount[],
        options: IDisplayOptions,
    ): BigNumber | undefined {
        if (fliAccounts == null) return null;
        let adjustmentValue: BigNumber | undefined;
        let reclassesValue: BigNumber | undefined;
        let reclassesAccountsOfFli: IAccount[] = [];

        if (options.showReClassInSums) {
            reclassesAccountsOfFli = reclassesAccounts;
            reclassesValue = this.calculateClosingReclassesOfAccounts(fliAccounts, reclassesAccountsOfFli);
        }

        if (options.showAdjustmentInSums) {
            adjustmentValue = this.calculateClosingAdjustmentsOfAccounts(fliAccounts, reclassesAccountsOfFli);
        }

        return NumberUtils.sumIgnoringInfiniteNumbers([adjustmentValue, reclassesValue], undefined, true);
    }

    private calculateOpeningAdjustmentsAndReclassesValueOfFli(
        fliAccounts: IAccount[],
        reclassesAccounts: IAccount[],
        options: IDisplayOptions,
    ): BigNumber | undefined {
        if (fliAccounts == null) return null;
        let adjustmentValue: BigNumber | undefined;
        let reclassesValue: BigNumber | undefined;
        let reclassesAccountsOfFli: IAccount[] = [];

        if (options.showReClassInSums) {
            reclassesAccountsOfFli = reclassesAccounts;
            reclassesValue = this.calculateOpeningReclassesValueOfAccounts(fliAccounts, reclassesAccountsOfFli);
        }

        if (options.showAdjustmentInSums) {
            adjustmentValue = this.calculateOpeningAdjustmentsOfAccounts(fliAccounts, reclassesAccountsOfFli);
        }

        return NumberUtils.sumIgnoringInfiniteNumbers([adjustmentValue, reclassesValue], undefined, true);
    }

    private calculateClosingAdjustmentsOfAccounts(
        accounts: IAccount[],
        reclassesAccounts: IAccount[],
    ): BigNumber | undefined {
        if (accounts == null) return null;

        const accountsClosingAdjustmentsValues = accounts.map<number | undefined>(
            (account) => account.closingAdjustment,
        );
        const totalClosingAdjustmentsOfAccounts = NumberUtils.sumIgnoringInfiniteNumbers(
            accountsClosingAdjustmentsValues,
            undefined,
            true,
        );

        let totalClosingAdjustmentsOfReclaasesAccounts: BigNumber | undefined;

        if (reclassesAccounts != null) {
            const reclassesAccountClosingAdjustmentsValues = reclassesAccounts.map<number | undefined>(
                (account) => account.closingAdjustment,
            );

            totalClosingAdjustmentsOfReclaasesAccounts = NumberUtils.sumIgnoringInfiniteNumbers(
                reclassesAccountClosingAdjustmentsValues,
                undefined,
                true,
            );
        }

        return NumberUtils.sumIgnoringInfiniteNumbers(
            [totalClosingAdjustmentsOfAccounts, totalClosingAdjustmentsOfReclaasesAccounts],
            undefined,
            true,
        );
    }

    private calculateOpeningAdjustmentsOfAccounts(
        accounts: IAccount[],
        reclassesAccounts: IAccount[],
    ): BigNumber | undefined {
        if (accounts == null) return null;

        const accountsOpeningAdjustmentsValues = accounts.map<number | undefined>(
            (account) => account.openingAdjustment,
        );
        const totalOpeningAdjustmentsOfAccounts = NumberUtils.sumIgnoringInfiniteNumbers(
            accountsOpeningAdjustmentsValues,
            undefined,
            true,
        );

        let totalOpeningAdjustmentsOfReclaasesAccounts: BigNumber | undefined;

        if (reclassesAccounts != null) {
            const reclassesAccountOpeningAdjustmentsValue = reclassesAccounts.map<number | undefined>(
                (account) => account.openingAdjustment,
            );

            totalOpeningAdjustmentsOfReclaasesAccounts = NumberUtils.sumIgnoringInfiniteNumbers(
                reclassesAccountOpeningAdjustmentsValue,
                undefined,
                true,
            );
        }

        return NumberUtils.sumIgnoringInfiniteNumbers(
            [totalOpeningAdjustmentsOfAccounts, totalOpeningAdjustmentsOfReclaasesAccounts],
            undefined,
            true,
        );
    }

    private calculateClosingReclassesOfAccounts(
        accounts: IAccount[],
        reclassesAccounts: IAccount[],
    ): BigNumber | undefined {
        if (accounts == null) return null;
        const accountsClosingReclassesValues = accounts.map<number | undefined>((account) => account.closingReClass);

        const totalClosingReclassesOfAccounts = NumberUtils.sumIgnoringInfiniteNumbers(
            accountsClosingReclassesValues,
            undefined,
            true,
        );

        let totalClosingReclassesOfReclaasesAccounts: BigNumber | undefined;

        if (reclassesAccounts != null) {
            const reclassesAccountsClosingReclassesValues = reclassesAccounts.map<number | undefined>(
                (account) => account.closingReClass,
            );

            totalClosingReclassesOfReclaasesAccounts = NumberUtils.sumIgnoringInfiniteNumbers(
                reclassesAccountsClosingReclassesValues,
                undefined,
                true,
            );
        }

        return NumberUtils.sumIgnoringInfiniteNumbers(
            [totalClosingReclassesOfAccounts, totalClosingReclassesOfReclaasesAccounts],
            undefined,
            true,
        );
    }

    private calculateOpeningReclassesValueOfAccounts(
        accounts: IAccount[],
        reclassesAccounts: IAccount[],
    ): BigNumber | undefined {
        const accountsOpeningReclassesValues = accounts.map<number | undefined>((account) => account.openingReClass);

        const totalOpeningReclassesOfAccounts = NumberUtils.sumIgnoringInfiniteNumbers(
            accountsOpeningReclassesValues,
            undefined,
            true,
        );

        let totalOfOpeningReclassesOfReclaasesAccounts: BigNumber | undefined;

        if (reclassesAccounts != null) {
            const reclassesAccountsOpeningReclassesValues = reclassesAccounts.map<number | undefined>(
                (account) => account.openingReClass,
            );

            totalOfOpeningReclassesOfReclaasesAccounts = NumberUtils.sumIgnoringInfiniteNumbers(
                reclassesAccountsOpeningReclassesValues,
                undefined,
                true,
            );
        }

        return NumberUtils.sumIgnoringInfiniteNumbers(
            [totalOpeningReclassesOfAccounts, totalOfOpeningReclassesOfReclaasesAccounts],
            undefined,
            true,
        );
    }

    private calculateTotalClosingValueOfAccounts(fliAccounts: IAccount[] | undefined): BigNumber | undefined {
        let accountsCalculatedClosingBalanceValues: Array<number | undefined> = [];

        if (fliAccounts != null) {
            accountsCalculatedClosingBalanceValues = fliAccounts.map<number | undefined>(
                (account) => account.calculatedClosingBalance,
            );
        }

        return NumberUtils.sumIgnoringInfiniteNumbers(accountsCalculatedClosingBalanceValues, undefined, true);
    }

    private calculateTotalOpeningValueOfAccounts(fliAccounts: IAccount[] | undefined): BigNumber | undefined {
        let accountsCalculatedOpeningBalanceValues: Array<number | undefined> = [];

        if (fliAccounts != null) {
            accountsCalculatedOpeningBalanceValues = fliAccounts.map<number | undefined>(
                (account) => account.calculatedOpeningBalance,
            );
        }

        return NumberUtils.sumIgnoringInfiniteNumbers(accountsCalculatedOpeningBalanceValues, undefined, true);
    }
}
